import { expense } from '@/db/schema/expense'
import { user } from '@/db/schema/user'
import { decryptRefreshToken, encryptRefreshToken } from '@/lib/revolut/crypto'
import { RevolutTransaction } from '@/lib/revolut/types'
import { relations } from 'drizzle-orm'
import { customType, decimal, index, integer, jsonb, PgColumn, pgTable, serial, text, timestamp } from 'drizzle-orm/pg-core'

/** `text` column that is encrypted at rest: app sees plaintext, the DB only ever holds ciphertext. */
const encryptedText = customType<{ data: string; driverData: string }>({
  dataType: () => 'text',
  toDriver: (value: string) => encryptRefreshToken(value),
  fromDriver: (value: string) => decryptRefreshToken(value),
})

/**
 * Maps a Revolut card (virtual or physical) to a schaltapp user.
 * Multiple rows per user are allowed. `revolutCardId` is the natural
 * lookup key from webhook/cron payloads.
 */
export const revolutCard = pgTable(
  'revolut_card',
  {
    id: serial('id').primaryKey(),
    created: timestamp().defaultNow().notNull(),
    updated: timestamp().defaultNow().notNull(),
    revolutCardId: text('revolut_card_id').notNull().unique(),
    userId: integer('user_id')
      .notNull()
      .references((): PgColumn => user.id, { onDelete: 'cascade' }),
    lastFour: text('last_four'),
    cardholderName: text('cardholder_name'),
    type: text('type').notNull(),
    state: text('state').notNull(),
  },
  (t) => ({
    userIdx: index('revolut_card_user_idx').on(t.userId),
  })
)

export type RevolutCardRow = typeof revolutCard.$inferSelect
export type InsertRevolutCard = typeof revolutCard.$inferInsert

/**
 * One row per Revolut transaction seen. Exists independently of the
 * linked expense so rejection/deletion does not break dedup.
 */
export const revolutTransaction = pgTable(
  'revolut_transaction',
  {
    id: serial('id').primaryKey(),
    created: timestamp().defaultNow().notNull(),
    updated: timestamp().defaultNow().notNull(),
    revolutTransactionId: text('revolut_transaction_id').notNull().unique(),
    revolutCardId: text('revolut_card_id'),
    type: text('type').notNull(),
    state: text('state').notNull(),
    startedAt: timestamp('started_at').defaultNow().notNull(),
    completedAt: timestamp('completed_at'),
    // Settlement movement on the account (e.g. -39.57 CHF).
    amount: decimal('amount').notNull(),
    currency: text('currency').notNull(),
    // Original/merchant side of a cross-currency leg (e.g. -48.65 USD).
    origAmount: decimal('orig_amount'),
    origCurrency: text('orig_currency'),
    exchangeRate: decimal('exchange_rate'),
    // `amount` plus any Revolut fee, same sign (e.g. -17.17 for a -17.00 leg + 0.17 fee).
    totalAmount: decimal('total_amount'),
    fee: decimal('fee'),
    feeCurrency: text('fee_currency'),
    balanceAfter: decimal('balance_after'),
    description: text('description'),
    reference: text('reference'),
    merchantName: text('merchant_name'),
    merchantMcc: text('merchant_mcc'),
    cardLastFour: text('card_last_four'),
    counterpartyName: text('counterparty_name'),
    counterpartyIban: text('counterparty_iban'),
    counterpartyAccount: text('counterparty_account'),
    counterpartyBic: text('counterparty_bic'),
    relatedTransactionId: text('related_transaction_id'),
    payload: jsonb('payload').$type<RevolutTransaction>().notNull(),
    expenseId: integer('expense_id').references((): PgColumn => expense.id, { onDelete: 'set null' }),
    rejectedAt: timestamp('rejected_at'),
    rejectedReason: text('rejected_reason'),
  },
  (t) => ({
    cardIdx: index('revolut_transaction_card_idx').on(t.revolutCardId),
    completedAtIdx: index('revolut_transaction_completed_at_idx').on(t.completedAt),
    typeIdx: index('revolut_transaction_type_idx').on(t.type),
  })
)

export type RevolutTransactionRow = typeof revolutTransaction.$inferSelect
export type InsertRevolutTransaction = typeof revolutTransaction.$inferInsert

/**
 * App-enforced singleton (`id = 1`) holding the long-lived Revolut refresh
 * token, persisted here so an admin can re-connect from inside the app
 * instead of editing a secret. `getAccessToken()` reads this first and falls
 * back to `process.env.REVOLUT_REFRESH_TOKEN` (first deploy / local dev).
 */
export const revolutIntegration = pgTable('revolut_integration', {
  id: integer('id').primaryKey(),
  refreshToken: encryptedText('refresh_token').notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
  updatedByUserId: integer('updated_by_user_id').references((): PgColumn => user.id, { onDelete: 'set null' }),
})

export type RevolutIntegrationRow = typeof revolutIntegration.$inferSelect
export type InsertRevolutIntegration = typeof revolutIntegration.$inferInsert

// Drizzle relations — lets callers do db.query.revolutTransaction.findFirst({ with: { expense: true } })
// (and the back-reference for db.query.expense.findFirst({ with: { revolutTransaction: true } })).
export const revolutTransactionRelations = relations(revolutTransaction, ({ one }) => ({
  expense: one(expense, { fields: [revolutTransaction.expenseId], references: [expense.id] }),
}))

export const expenseRevolutTransactionRelation = relations(expense, ({ one }) => ({
  revolutTransaction: one(revolutTransaction, { fields: [expense.id], references: [revolutTransaction.expenseId] }),
}))
